import { JsonForms } from '@jsonforms/react';
import { materialCells, materialRenderers } from '@jsonforms/material-renderers';
import { createAjv } from '@jsonforms/core';
import ajvKeywords from 'ajv-keywords';
import { ThemeProvider, createTheme, darken, lighten, useTheme } from '@mui/material';
import { palette } from '../../../../theme/palette';
// import { AssetSchema, AssetUISchema } from '../panel-schemas';

import MaterialCategorizationStepperLayoutRenderer, {
  materialCategorizationStepperTester,
} from '../custom-renderers/stepper/stepper';
import secretAutoComplete, { secretAutoCompleteTester } from '../custom-renderers/secretAutoComplete';
import MaterialEnumCell, { materialEnumCellTester } from '../custom-renderers/cellEnum';
import { get } from 'lodash';
import { useOutletContext } from 'react-router-dom';
import lightOrdarkColor from '@check-light-or-dark/color';
import ajvErrors from 'ajv-errors';
import { customLabelTextTester } from '../custom-renderers/customTextField';
import CustomLabelTextControl from '../custom-renderers/customTextField/component';
// import { autocompleteControl } from '../custom-renderers/autocompleteField';

import RootTagControl, { RootTagTester } from '../custom-renderers/rootTag';
import CopyConfigurationRenderer, { CopyConfigurationTester } from '../custom-renderers/copyConfiguration';
import CellPropTextField, { cellPropTextFieldTester } from '../custom-renderers/cellPropTextField';
import SelectFactoryConfigurationRenderer, { SelectFactoryConfigurationTester } from '../custom-renderers/selectFactoryConfiguration';

const ajv = createAjv({
  allErrors: true,
  verbose: false,
  strict: false,
  inlineRefs: false,
});

ajv.addKeyword({
  keyword: 'customRequired',
  type: 'object',
  schemaType: 'array',
  compile: (schemaVal) => {
    const validate = function (data: any, schema: any) {
      if (!schemaVal || !Array.isArray(schemaVal)) return true;

      const errors: any[] = [];
      schemaVal.forEach((fieldName) => {
        if (fieldName === 'value') {
          if (data.type !== 'trigger' || (data.type === 'trigger' && data.comparison === 'changes')) {
            return;
          }
        }

        if (!data[fieldName] || (typeof data[fieldName] === 'string' && data[fieldName].trim() === '')) {
          errors.push({
            instancePath: `/${fieldName}`,
            keyword: 'customRequired',
            message: `Is is a required property`,
            params: { missingProperty: fieldName },
          });
        }
      });

      if (errors.length > 0) {
        (validate as any).errors = errors;
        return false;
      }
      (validate as any).errors = null;
      return true;
    };
    return validate;
  },
});

ajvErrors(ajv);
ajvKeywords(ajv, 'uniqueItemProperties');

const PRIMARY_COLOR = '#5C85FF';

const JsonFormsWrapper = ({
  schema,
  uiSchema,
  data,
  readOnly,
  onChange,
  additionalErrors,
  hasErrors,
  includeFlowDependentRenderers = true,
  includeCustomRenderers = true,
  includeCustomCells = true,
}: any) => {
  const appTheme: any = useTheme();
  const themeMode: any = get(useOutletContext(), 'themeMode', {});
  const theme = createTheme({
    palette: {
      primary: {
        main: appTheme.palette.primary.main,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            textWrap: 'nowrap',
            padding: '9px 16px',
            gap: '8px',
            borderRadius: '8px',
            alignItems: 'center',
            fontStyle: 'SemiBold',
            fontWeight: 600,
            lineHeight: '22px',
            letterSpacing: '5%',
            fontSize: '1rem',
            boxShadow: 'none',
            color: theme.palette.primary.main,

            '&:hover': {
              boxShadow: 'none',
            },

            '&.Mui-disabled': {
              borderColor: palette.disabledColor.contrastText,
              backgroundColor:
                lightOrdarkColor(theme.palette.background.default) === 'dark'
                  ? palette.disabledColor.dark
                  : palette.disabledColor.light,
              color: palette.disabledColor.contrastText,
            },
          }),
          outlined: ({ theme }: any) => ({
            '&:hover': { backgroundColor: lighten(theme.palette.primary.light, 0.5) },
          }),

          contained: ({ theme }: any) => ({
            color: lighten(theme.palette.background.default, 0.9),
            '&:hover': {
              backgroundColor: lighten(theme.palette.primary.light, 0.5),
              color: theme.palette.primary.main,
            },
          }),
        },
      },

      MuiInputLabel: {
        defaultProps: { shrink: true },
        styleOverrides: {
          root: {
            marginTop: 16,
            marginBottom: -16,
            marginLeft: -12,
            fontSize: 20,
            color: appTheme.palette.text.primary,
            fontFamily: 'Open sans',
            position: 'relative',
            whiteSpace: 'initial',
            width: '130%',
            '&.Mui-disabled': {
              color: appTheme.palette.text.primary,
            },
            // width: 250,
            // textOverflow: 'ellipsis',
            // overflow: 'hidden',
            // textWrap: 'nowrap',
            // textAlign: 'end',
            // paddingRight: 24,

            // '&::after': {
            //   content: '":"',
            //   position: 'absolute',
            //   right: '16px',
            // },

            '&.Mui-focused': {
              color: appTheme.palette.text.primary,
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            // marginTop: '22px',
            // fontSize: 18,
            // width: 250,
            // textOverflow: 'ellipsis',
            // overflow: 'hidden',
            // textWrap: 'nowrap',
            fontWeight: 600,
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            '&.Mui-disabled': {
              backgroundColor:
                lightOrdarkColor(appTheme.palette.background.default) === 'dark'
                  ? palette.disabledColor.dark
                  : palette.disabledColor.light,
            },
            input: {
              '&:-internal-autofill-selected': {
                // backgroundColor: 'red !important',
              },
              '&.Mui-disabled': {
                WebkitTextFillColor: palette.disabledColor.contrastText,
              },
            },
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            'label + &': {
              span: {
                display: 'none',
              },
            },
            // '&.MuiInputBase-root': {
            //   width: '70%',
            //   display: 'flex',
            //   alignSelf: 'flex-end',
            // },

            height: '42px',
            gap: '10px',
            fontWeight: 400,
            color: palette.info.dark,
            backgroundColor: palette.primary.contrastText,
            borderRadius: '8px',
            marginTop: 4,

            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: darken(appTheme.palette.background.default, 0.2),
            },

            '&:hover .MuiOutlinedInput-notchedOutline ': {
              borderColor: darken(appTheme.palette.background.default, 0.2),
            },

            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline ': {
                borderColor: darken(appTheme.palette.background.default, 0.2),
              },
            },

            '&.Mui-error': {
              '&:hover .MuiOutlinedInput-notchedOutline ': {
                borderColor: palette.error.main,
              },
            },

            '&.Mui-error.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline ': {
                borderColor: palette.error.main,
              },
            },

            // '&.Mui-disabled .MuiSelect-select': {
            //   WebkitTextFillColor: palette.disabled.contrastText,
            // },
            //not able to see the text color in disabled condition
            '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
              borderColor:
                lightOrdarkColor(theme.palette.background.default) === 'dark'
                  ? lighten(theme.palette.background.default, 0.6)
                  : darken(theme.palette.background.default, 0.2),
            },
            '& .MuiSvgIcon-root': {
              color: palette.info.dark,
              opacity: '0.7',
            },
          }),
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {            
            '& .MuiTableHead-root .MuiTableCell-root:last-child, & .MuiTableBody-root .MuiTableCell-root:last-child': {
              paddingBottom: '24px !important',
            },


          }
        }
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&.MuiTableRow-hover': {
              '&:hover': {
                backgroundColor: 'transparent',
              },
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '8px 12px',
            // width: '25%',

            // '& .MuiInputBase-root': {
            //   width: '100%',
            // },            
            '& .MuiBadge-root': {
              color: palette.error.main,
            },
            '& .MuiBadge-badge': {
              color: 'transparent',
            },
            // '& .MuiAutocomplete-root': {
            //   width: '180px',
            // },
            '.MuiTableBody-root &.MuiTableCell-body': {
              padding: '0px 12px !important',
            },
            '.MuiTableBody-root .MuiTableRow-root:first-of-type &.MuiTableCell-body': {
              paddingTop: '8px !important',
            },

            '&:first-child': {
              width: '35%',
            },


            // Hide only the helper injected directly by table-cell wrappers,
            // while preserving nested control helper text.
            '& > .MuiFormHelperText-root.Mui-error': {
              display: 'none',
            },
          },
          head: {
            textTransform: 'uppercase',
            borderBottom: `1px solid ${appTheme.palette.customBorderColor.main}`,
          },
        },
      },
      MuiAccordion: {
        defaultProps: {
          TransitionProps: {
            unmountOnExit: true,
          },
        },
        styleOverrides: {
          root: {
            boxShadow: 'none',
            marginBottom: 12,
            border: `2px solid ${appTheme.palette.customBorderColor.main}`,
            borderRadius: 12,

            '&:last-of-type, &:first-of-type': {
              borderRadius: 12,
            },

            '&::before': {
              display: 'none',
            },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            backgroundColor: themeMode.isLightBG
              ? lighten(appTheme.palette.background.default, 0.2)
              : lighten(appTheme.palette.background.default, 0.08),

            borderRadius: 9,
            padding: '0px 12px',

            flexDirection: 'row-reverse',

            '&.Mui-expanded': {
              // borderRadius: 0,
              borderBottom: `2px solid ${appTheme.palette.customBorderColor.main}`,
              borderBottomRightRadius: 0,
              borderBottomLeftRadius: 0,
            },

            '& .MuiSvgIcon-root': {
              color: appTheme.palette.custom.light,
            },

            '& .MuiGrid-root.MuiGrid-grid-xs-10': {
              paddingLeft: '8px',
            },

            '& .MuiAvatar-root': {
              display: 'none',
            },
          },
          content: {
            margin: '8px 0px',

            '&.Mui-expanded': {
              margin: '8px 0px',
            },
          },
          expandIconWrapper: {
            border: `2px solid ${appTheme.palette.custom.light}`,
            borderRadius: '6px',
            height: '24px',
            width: '24px',
          },
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            backgroundColor: themeMode.isLightBG
              ? lighten(appTheme.palette.background.default, 0.9)
              : lighten(appTheme.palette.background.default, 0.05),
            borderRadius: '0 0 9px 9px',
            padding: '24px',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            '&[aria-label^="Add to"]': {
              border: `1px solid ${lightOrdarkColor(appTheme.palette.background.default) === 'dark'
                ? lighten(appTheme.palette.primary.dark, 0.06)
                : lighten(appTheme.palette.primary.light, 0.2)
                }`,
              borderRadius: '8px',
              padding: '9px 16px',
              fontFamily: 'Open Sans',
              '&:hover': {
                border: `1px solid ${appTheme.palette.primary.main}`,
                backgroundColor: lighten(appTheme.palette.primary.light, 0.5),
              },
              '& .MuiSvgIcon-root': {
                color: appTheme.palette.primary.main,
                marginRight: '8px',
                borderRadius: '50px',
                border: `2px solid ${appTheme.palette.primary.main}`,
                fontSize: '16px',
              },

              '&::after': {
                content: '"ADD"',
                color: appTheme.palette.primary.main,
                fontSize: '16px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              },
            },

            '&[aria-label^="Add to Message"]': {
              border: `1px solid ${lightOrdarkColor(appTheme.palette.background.default) === 'dark'
                ? lighten(appTheme.palette.primary.dark, 0.06)
                : lighten(appTheme.palette.primary.light, 0.2)
                }`,
              borderRadius: '8px',
              padding: '9px 16px',
              fontFamily: 'Open Sans',
              '&:hover': {
                border: `1px solid ${appTheme.palette.primary.main}`,
                backgroundColor: lighten(appTheme.palette.primary.light, 0.5),
              },

              '& .MuiSvgIcon-root': {
                color: appTheme.palette.primary.main,
                marginRight: '8px',
                borderRadius: '50px',
                border: `2px solid ${appTheme.palette.primary.main}`,
                fontSize: '16px',
              },

              '&::after': {
                content: '"ADD MESSAGE"',
                color: appTheme.palette.primary.main,
                fontSize: '16px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              },
            },
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          circular: {
            backgroundColor: 'transparent',
            color: appTheme.palette.custom.light,
            border: `2px solid ${appTheme.palette.custom.light}`,
            borderRadius: '8px',
            maxWidth: '35px',
            height: '25px',
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: PRIMARY_COLOR,
          },
        },
      },
      MuiGrid: {
        styleOverrides: {
          item: {
            color: appTheme.palette.custom.light,
            fontFamily: 'Open sans',
            fontWeight: 600,
            '& .MuiBadge-root': {
              color: palette.error.main,
            },
            '& .MuiBadge-badge': {
              color: 'transparent',
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            marginBottom: 6,
          },
          paper: {
            color: palette.info.dark,
            border: `2px solid ${darken(appTheme.palette.background.default, 0.2)}`,
            borderRadius: 12,
            boxShadow: 'none',
          },
          // popper: {
          //   width: '500px !important',
          // },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: palette.secondary.dark,
          },
          arrow: {
            color: palette.secondary.dark,
          },
        },
      },
      MuiStack: {
        styleOverrides: {
          root: {
            width: '100%',
          },
        },
      },
    },
    typography: {
      allVariants: {
        color: appTheme.palette.text.primary,
        fontFamily: 'Open sans',
      },
      h6: {
        fontSize: 18,
        fontWeight: 600,
        fontFamily: 'Open sans',
        textTransform: 'capitalize',
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <JsonForms
        schema={schema}
        uischema={uiSchema}
        // schema={AssetSchema}
        // uischema={AssetUISchema}
        data={data}
        readonly={readOnly}
        additionalErrors={additionalErrors}
        renderers={[
          ...materialRenderers,
          ...(includeCustomRenderers
            ? [
              { tester: materialCategorizationStepperTester, renderer: MaterialCategorizationStepperLayoutRenderer },
              { tester: secretAutoCompleteTester, renderer: secretAutoComplete },
              { tester: customLabelTextTester, renderer: CustomLabelTextControl },
              { tester: RootTagTester, renderer: RootTagControl },
              { tester: SelectFactoryConfigurationTester, renderer: SelectFactoryConfigurationRenderer },
            ]
            : []),
          ...(includeFlowDependentRenderers && includeCustomRenderers
            ? [{ tester: CopyConfigurationTester, renderer: CopyConfigurationRenderer }]
            : []),
        ]}
        cells={[
          ...materialCells,
          ...(includeCustomCells ? [{ tester: materialEnumCellTester, cell: MaterialEnumCell }] : []),
          { tester: cellPropTextFieldTester, cell: CellPropTextField },
        ]}
        ajv={ajv}
        onChange={onChange}
      />
    </ThemeProvider>
  );
};

export default JsonFormsWrapper;
